import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function ActivityModal({ isOpen, onClose, activity }) {
  const [isAdding, setIsAdding] = useState(false);
  const { family, familyId } = useFamily();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!isOpen || !activity) return null;

  const handleAddToExperience = () => {
    // Navigate to experience planning or create new experience
    navigate('/');
    toast.success('Activity saved! Create an experience to add it.');
    onClose();
  };

  const handleMarkAsDone = async () => {
    if (!familyId || !user) {
      toast.error('Please sign in to mark activities as done');
      return;
    }

    setIsAdding(true);
    try {
      const familyRef = doc(db, 'families', familyId);
      await updateDoc(familyRef, {
        completedActivities: arrayUnion({
          activityId: activity.id,
          completedAt: new Date(),
          completedBy: user.uid,
        }),
      });
      toast.success('Activity marked as done!');
      onClose();
    } catch (error) {
      console.error('Error marking activity as done:', error);
      toast.error('Failed to mark activity as done');
    } finally {
      setIsAdding(false);
    }
  };

  const isCompleted = family?.completedActivities?.some(
    a => a.activityId === activity.id
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
              {activity.title}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {/* Activity Info */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <div className="text-text-secondary mb-1">Age Range</div>
                <div className="font-semibold text-text-primary">{activity.ageRange}</div>
              </div>
              <div>
                <div className="text-text-secondary mb-1">Time</div>
                <div className="font-semibold text-text-primary">{activity.time}</div>
              </div>
              <div>
                <div className="text-text-secondary mb-1">Category</div>
                <div className="font-semibold text-text-primary capitalize">{activity.category}</div>
              </div>
            </div>

            {activity.description && (
              <div>
                <div className="text-sm text-text-secondary mb-2">Description</div>
                <div className="text-base text-text-primary">{activity.description}</div>
              </div>
            )}

            {/* Materials */}
            {activity.materials && activity.materials.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-text-primary mb-2">Materials Needed</div>
                <ul className="list-disc list-inside space-y-1 text-sm text-text-primary">
                  {activity.materials.map((material, index) => (
                    <li key={index}>{material}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions */}
            {activity.instructions && activity.instructions.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-text-primary mb-2">Instructions</div>
                <ol className="list-decimal list-inside space-y-2 text-sm text-text-primary">
                  {activity.instructions.map((step, index) => (
                    <li key={index} className="leading-relaxed">{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {isCompleted ? (
              <div className="px-4 py-3 bg-primary/10 text-primary rounded-xl font-semibold text-center flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={faCheck} />
                <span>Completed</span>
              </div>
            ) : (
              <button
                onClick={handleMarkAsDone}
                disabled={isAdding}
                className="w-full px-4 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCheck} />
                <span>{isAdding ? 'Marking...' : 'Mark as Done'}</span>
              </button>
            )}
            <button
              onClick={handleAddToExperience}
              className="w-full px-4 py-3 bg-white border-2 border-primary text-primary rounded-xl font-semibold hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add to Experience</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

